import { ReactNode } from 'react';
import genCommonStyles from './generator-layout.module.scss';
import styles from './css-generator-layout.module.scss';
import { CodeBlock } from './code-block';

export interface CssGeneratorLayoutProps {
    // Controls (left column). Use genStyles.section etc. as building blocks.
    controls: ReactNode;

    // Visual preview — typically a div with the generated CSS applied.
    previewLabel: ReactNode;
    previewMeta?: ReactNode;
    preview: ReactNode;

    // Code output. Pass a string + language for syntax-highlighted rendering;
    // pass a ReactNode for custom layout (rare).
    codeLabel: ReactNode;
    codeMeta?: ReactNode;
    code: ReactNode | string;
    // Prism language id (css/javascript/json/html/...). Defaults to 'css'.
    codeLanguage?: string;
    codeActions?: ReactNode;
}

// Layout for CSS / visual generators (gradient, box-shadow, neumorphism, ...)
// Left column: controls. Right column: visual preview on top, CSS code below,
// both sticky as a unit. On narrow viewports (<992px) it collapses to a
// single column stack.
export function CssGeneratorLayout(props: CssGeneratorLayoutProps) {
    const isString = typeof props.code === 'string';
    const lang = props.codeLanguage ?? 'css';

    return (
        <div className={styles.grid}>
            <div className={styles.controls}>{props.controls}</div>

            <div className={styles.right}>
                <div className={styles.previewSection}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionLabel}>{props.previewLabel}</span>
                        {props.previewMeta && <span className={styles.sectionMeta}>{props.previewMeta}</span>}
                    </div>
                    <div className={styles.previewSurface}>{props.preview}</div>
                </div>

                <div className={styles.codeSection}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionLabel}>{props.codeLabel}</span>
                        {props.codeMeta && <span className={styles.sectionMeta}>{props.codeMeta}</span>}
                    </div>
                    {isString ? (
                        <CodeBlock code={props.code as string} language={lang} wrapLines maxHeight={220} />
                    ) : (
                        <pre className={styles.codeBlock}>{props.code}</pre>
                    )}
                    {props.codeActions && <div className={styles.codeActions}>{props.codeActions}</div>}
                </div>
            </div>
        </div>
    );
}

export const cssGenStyles = styles;
// Re-export the shared form primitives so consumers only need one import.
export const cssGenCommon = genCommonStyles;
